import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Modal, Button } from "react-bootstrap";
import { Clock, User, CheckSquare, ExternalLink, Briefcase, Tool, DollarSign } from "react-feather";
import InfoTile from "../../../../components/common/InfoTile";
import NavigateLink from "../../../../components/common/NavigateLink";
import { MAROON } from "../../../../components/common/brandColors";
import ListExportToolbar from "../../../stock/components/list/ListExportToolbar";
import EmployeeAvatar from "../../../reports/oneStop/components/EmployeeAvatar";
import { useEmployeePhotoCache } from "../../../masters/employees/useEmployeePhotoCache";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { useGetClientVisitMonthGridQuery, useGetClientVisitQuery, useListVisitActivityQuery } from "../../salesApi";
import ClientVisitDetailModal from "../components/ClientVisitDetailModal";

// Monthly Visit Summary (Batch202 -> Batch203, 2026-10-09). "As the fuel summary report, we need
// another report for client visit, same like days in month, grouped with designation and
// employees, count of each days visit and drill down ... client name, remarks and most
// importantly time of visit", then: include the Collections module's client visits (ONLY the
// no-payment "Visit" entries - real collections are not visits) and Technical.
//
// GET /sales/client-visits/month-grid returns one row per person per source per day; pivoted
// here into designation -> person -> one line per source -> day 1..31, like the fuel grid's
// Fuel / Card refill / Cash lines. Any count opens the visits behind it (GET .../activity) in
// time order. The time is labelled by what it really is: on site (client visit, GPS), logged
// in the app (collection visit) or report filed (technical) - an office entry must not read
// as an arrival time.

export const VISIT_SOURCES = {
  SALES: { label: "Client visit", plural: "Client visits", short: "Client visit", color: "#6A3FB5", tint: "#EFE9FA", Icon: Briefcase },
  COLLECTIONS: { label: "Collection visit", plural: "Collection visits", short: "Collection", color: "#0E7C86", tint: "#E3F4F5", Icon: DollarSign },
  TECHNICAL: { label: "Technical", plural: "Technical reports", short: "Technical", color: "#B0570B", tint: "#FBEEE3", Icon: Tool },
};
const SOURCE_ORDER = ["SALES", "COLLECTIONS", "TECHNICAL"];
// Technical job kinds (App_TechnicalWork.SourceType) as people say them.
const JOB_KIND = { COMPLAINT: "Complaint", SERVICE_VISIT: "Service visit", SALES_ORDER: "Installation" };

const PERSON_W = 200;
const SOURCE_W = 100;
const TOTAL_W = 64;
const PTOTAL_W = 84;

// Day cell shade: the source's own colour, deeper with more visits.
const heat = (source, n) => {
  if (!n) return {};
  const c = VISIT_SOURCES[source]?.color || MAROON;
  if (n === 1) return { background: VISIT_SOURCES[source]?.tint, color: c };
  if (n <= 3) return { background: `${c}40`, color: c };
  return { background: c, color: "#fff" };
};

const buildGrid = (rows, daysInMonth) => {
  const emptyDays = () => Array.from({ length: daysInMonth + 1 }, () => 0);
  const groups = new Map();
  for (const r of rows || []) {
    const gKey = r.designationName || "(no designation)";
    if (!groups.has(gKey)) groups.set(gKey, { designation: gKey, people: new Map() });
    const g = groups.get(gKey);
    if (!g.people.has(r.personKey)) {
      g.people.set(r.personKey, { key: r.personKey, name: r.personName, employeeGuid: r.employeeRecordGuid, designation: gKey, lines: {}, total: 0 });
    }
    const p = g.people.get(r.personKey);
    if (!p.lines[r.source]) p.lines[r.source] = { days: emptyDays(), total: 0 };
    if (r.dayOfMonth >= 1 && r.dayOfMonth <= daysInMonth) p.lines[r.source].days[r.dayOfMonth] += r.visitCount;
    p.lines[r.source].total += r.visitCount;
    p.total += r.visitCount;
  }
  const sum = (people) => {
    const lines = {};
    SOURCE_ORDER.forEach((s) => {
      const days = emptyDays();
      let total = 0;
      people.forEach((p) => {
        const l = p.lines[s];
        if (!l) return;
        l.days.forEach((n, d) => { days[d] += n; });
        total += l.total;
      });
      lines[s] = { days, total };
    });
    const allDays = emptyDays();
    SOURCE_ORDER.forEach((s) => lines[s].days.forEach((n, d) => { allDays[d] += n; }));
    return { lines, allDays, total: SOURCE_ORDER.reduce((t, s) => t + lines[s].total, 0) };
  };
  const out = [...groups.values()]
    .map((g) => {
      const people = [...g.people.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
      return { designation: g.designation, people, totals: sum(people) };
    })
    .sort((a, b) => b.totals.total - a.totals.total || a.designation.localeCompare(b.designation));
  const allPeople = out.flatMap((g) => g.people);
  return { groups: out, grand: sum(allPeople), allPeople };
};

const linesOf = (lines) => SOURCE_ORDER.filter((s) => lines[s] && lines[s].total > 0);
const activeDays = (p) => {
  const set = new Set();
  Object.values(p.lines).forEach((l) => l.days.forEach((n, d) => { if (n > 0) set.add(d); }));
  return set.size;
};

// The full client visit report (SALES only) - fetched on demand, the drill-down row is a summary.
const SalesVisitDetail = ({ recordGuid, photoUrl, onClose }) => {
  const { data } = useGetClientVisitQuery(recordGuid);
  return data ? <ClientVisitDetailModal visit={data} photoUrl={photoUrl} onClose={onClose} /> : null;
};

// Only a real on-site time is shown large (client visit GPS stamp, technical QR Time In). Without
// one the card says so and shows when the entry was saved instead (2026-10-09: technical "times"
// were report-save times - 11 reports saved 19:23-19:46 for jobs raised over a week).
const timeLabel = (v) => {
  const at = dayjs(v.activityAt);
  if (v.timeKnown) return { time: at.format("HH:mm"), note: v.source === "TECHNICAL" ? "checked in (QR)" : "on site" };
  return { time: "--:--", note: "visit time not recorded" };
};

const ClientVisitMonthlySummaryPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [monthValue, setMonthValue] = useState(dayjs().format("YYYY-MM"));
  const [source, setSource] = useState(""); // "" = all
  const [drill, setDrill] = useState(null); // { title, personKeys: string[] | null, day: number | null, source: string | null }
  const [openSalesGuid, setOpenSalesGuid] = useState(null);

  const month = dayjs(`${monthValue}-01`);
  const daysInMonth = month.daysInMonth();
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);
  const isWeekend = (d) => [0, 6].includes(month.date(d).day());
  const isToday = (d) => month.date(d).isSame(dayjs(), "day");

  const { data, isLoading, isFetching } = useGetClientVisitMonthGridQuery({
    year: month.year(),
    month: month.month() + 1,
    branchId: selectedBranchId ?? undefined,
  });
  // Fetched once for all sources; the source buttons filter here so switching is instant.
  const rows = useMemo(() => (data || []).filter((r) => !source || r.source === source), [data, source]);
  const grid = useMemo(() => buildGrid(rows, daysInMonth), [rows, daysInMonth]);
  const sourceTotals = useMemo(() => {
    const t = { SALES: 0, COLLECTIONS: 0, TECHNICAL: 0 };
    (data || []).forEach((r) => { t[r.source] = (t[r.source] || 0) + r.visitCount; });
    return t;
  }, [data]);
  const photoUrls = useEmployeePhotoCache(grid.allPeople, (p) => p?.employeeGuid);
  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const monthLabel = month.format("MMMM YYYY");
  const sourceLabel = source ? VISIT_SOURCES[source].plural : "All visits";
  const busiest = days.reduce((best, d) => (grid.grand.allDays[d] > (best ? grid.grand.allDays[best] : 0) ? d : best), null);

  // ---- drill-down ----
  const drillFrom = drill ? (drill.day ? month.date(drill.day) : month) : null;
  const drillTo = drill ? (drill.day ? month.date(drill.day).add(1, "day") : month.add(1, "month")) : null;
  const { data: drillData, isFetching: drillLoading } = useListVisitActivityQuery(
    drill
      ? {
          dateFrom: drillFrom.format("YYYY-MM-DD"),
          dateTo: drillTo.format("YYYY-MM-DD"),
          branchId: selectedBranchId ?? undefined,
          source: drill.source || source || undefined,
          personKey: drill.personKeys?.length === 1 ? drill.personKeys[0] : undefined,
        }
      : undefined,
    { skip: !drill }
  );
  const drillVisits = useMemo(() => {
    const wanted = drill?.personKeys && drill.personKeys.length > 1 ? new Set(drill.personKeys) : null;
    const list = (drillData || []).filter((v) => !wanted || wanted.has(v.personKey));
    // "Saved in a batch": another entry by the same person saved within 5 minutes - typed up
    // together afterwards, so the save times say nothing about when each site was visited.
    const byPerson = new Map();
    list.forEach((v) => {
      if (!byPerson.has(v.personKey)) byPerson.set(v.personKey, []);
      byPerson.get(v.personKey).push(dayjs(v.recordedAt).valueOf());
    });
    return list.map((v) => {
      const t = dayjs(v.recordedAt).valueOf();
      const batch = !v.timeKnown && byPerson.get(v.personKey).some((o) => o !== t && Math.abs(o - t) <= 5 * 60 * 1000);
      return { ...v, savedInBatch: batch };
    });
  }, [drillData, drill]);
  const drillPhotos = useEmployeePhotoCache(drillVisits, (v) => v?.employeeRecordGuid);
  const openDrill = (title, personKeys, day, src = null) => setDrill({ title, personKeys, day, source: src });
  const dayTitle = (d) => month.date(d).format("ddd DD MMM YYYY");

  // ---- exports: PDF = per-person counts by source (31 day columns do not fit a page); Excel = full grid ----
  const excelRows = grid.allPeople.flatMap((p) =>
    linesOf(p.lines).map((s) => ({ designation: p.designation, name: p.name, source: s, line: p.lines[s] }))
  );
  const excelColumns = [
    { header: "Person", value: (r) => r.name },
    { header: "Source", value: (r) => VISIT_SOURCES[r.source].plural },
    ...days.map((d) => ({ header: String(d), value: (r) => r.line.days[d] || "", align: "right", decimals: 0 })),
    { header: "Total", value: (r) => r.line.total, align: "right", decimals: 0 },
  ];
  const pdfColumns = [
    { header: "Person", value: (r) => r.name },
    ...SOURCE_ORDER.filter((s) => !source || s === source).map((s) => ({
      header: VISIT_SOURCES[s].plural, value: (r) => r.lines[s]?.total || "", align: "right", decimals: 0,
    })),
    { header: "Total", value: (r) => r.total, align: "right", decimals: 0 },
    { header: "Days active", value: (r) => activeDays(r), align: "right", decimals: 0 },
  ];

  const stickyLeft = (left, width, extra = {}) => ({
    position: "sticky", left, zIndex: 2, width, minWidth: width, maxWidth: width, ...extra, background: extra.background || "#fff",
  });
  const stickyRight = (right, width, extra = {}) => ({
    position: "sticky", right, zIndex: 2, width, minWidth: width, textAlign: "center", fontWeight: 700, ...extra, background: extra.background || "#fff",
  });
  const EDGE_L = { boxShadow: "inset -2px 0 0 #e3d3d6" };
  const EDGE_R = { boxShadow: "inset 2px 0 0 #e3d3d6" };
  const countCell = (key, d, n, src, onClick, bg) => (
    <td
      key={key}
      onClick={n ? onClick : undefined}
      title={n ? `${n} ${n === 1 ? "visit" : "visits"} - click for details` : undefined}
      style={{
        textAlign: "center", minWidth: 32, cursor: n ? "pointer" : "default", fontWeight: n ? 700 : 400,
        backgroundImage: !n && isWeekend(d) ? "linear-gradient(rgba(0,0,0,0.045), rgba(0,0,0,0.045))" : undefined,
        backgroundColor: !n ? bg : undefined,
        ...heat(src, n),
      }}
    >
      {n || ""}
    </td>
  );
  const colCount = days.length + 4;

  // A block of lines (one person, a designation subtotal, the grand total): the label and the
  // block total span the lines, one line per source with visits.
  const renderBlock = ({ key, label, name, lines, total, personKeys, bg, strong }) => {
    const who = name || label;
    const shown = linesOf(lines);
    if (!shown.length) return null;
    return shown.map((s, idx) => {
      const meta = VISIT_SOURCES[s];
      return (
        <tr key={`${key}-${s}`} style={strong ? { fontWeight: 700 } : undefined}>
          {idx === 0 && (
            <td rowSpan={shown.length} style={stickyLeft(0, PERSON_W, { verticalAlign: "middle", background: bg })}>{label}</td>
          )}
          <td
            title={meta.plural}
            style={stickyLeft(PERSON_W, SOURCE_W, { ...EDGE_L, color: meta.color, fontWeight: 600, background: bg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" })}
          >
            <meta.Icon size={11} className="me-1" />{meta.short}
          </td>
          {days.map((d) => countCell(d, d, lines[s].days[d], s, () => openDrill(`${who} - ${meta.plural} - ${dayTitle(d)}`, personKeys, d, s), bg))}
          <td
            style={stickyRight(PTOTAL_W, TOTAL_W, { ...EDGE_R, color: meta.color, cursor: "pointer", background: bg })}
            onClick={() => openDrill(`${who} - ${meta.plural} - ${monthLabel}`, personKeys, null, s)}
          >
            {lines[s].total}
          </td>
          {idx === 0 && (
            <td
              rowSpan={shown.length}
              // "All sources" = the person's lines added up; blank when there is only one line,
              // where it would just repeat Total.
              style={stickyRight(0, PTOTAL_W, { verticalAlign: "middle", color: MAROON, fontSize: 14, cursor: shown.length > 1 ? "pointer" : "default", background: bg })}
              onClick={shown.length > 1 ? () => openDrill(`${who} - ${monthLabel}`, personKeys, null) : undefined}
            >
              {shown.length > 1 ? total : ""}
            </td>
          )}
        </tr>
      );
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Monthly Visit Summary</h4>
            <h6>Client visits, collection visits and technical service reports per person per day, grouped by designation - click any count for the visits, times and remarks</h6>
          </div>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6 col-md"><InfoTile label="Month" value={monthLabel} /></div>
          <div className="col-6 col-md"><InfoTile label="People" value={grid.allPeople.length} /></div>
          {SOURCE_ORDER.map((s) => (
            <div className="col-6 col-md" key={s}>
              <InfoTile label={VISIT_SOURCES[s].plural} active={source === s} onClick={() => setSource(source === s ? "" : s)} title="Click to show only these">
                <span style={{ color: VISIT_SOURCES[s].color }}>{sourceTotals[s] || 0}</span>
              </InfoTile>
            </div>
          ))}
          <div className="col-6 col-md">
            <InfoTile
              label="Busiest day"
              tone="success"
              value={busiest ? `${month.date(busiest).format("ddd DD")} (${grid.grand.allDays[busiest]})` : "-"}
              onClick={busiest ? () => openDrill(`All visits - ${dayTitle(busiest)}`, null, busiest) : undefined}
            />
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2" style={{ rowGap: 8 }}>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <label className="small text-muted mb-0">Month</label>
                <input
                  type="month"
                  className="form-control form-control-sm"
                  style={{ width: 170, height: 34 }}
                  value={monthValue}
                  max={dayjs().format("YYYY-MM")}
                  onChange={(e) => e.target.value && setMonthValue(e.target.value)}
                />
                <div className="btn-group btn-group-sm ms-2" role="group">
                  <button type="button" className="btn" style={source === "" ? { background: MAROON, color: "#fff" } : { border: "1px solid #ddd" }} onClick={() => setSource("")}>All</button>
                  {SOURCE_ORDER.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="btn"
                      style={source === s ? { background: VISIT_SOURCES[s].color, color: "#fff" } : { border: "1px solid #ddd", color: VISIT_SOURCES[s].color, fontWeight: 600 }}
                      onClick={() => setSource(s)}
                    >
                      {VISIT_SOURCES[s].plural}
                    </button>
                  ))}
                </div>
              </div>
              <ListExportToolbar
                title={`Monthly Visit Summary - ${monthLabel}`}
                subtitleLines={[`Branch: ${branchCode || "All"}`, `Showing: ${sourceLabel}`, "Collection visits = no-payment \"Visit\" entries only; technical = service reports."]}
                branchCode={branchCode}
                columns={excelColumns}
                rows={excelRows}
                pdfColumns={pdfColumns}
                pdfRows={grid.allPeople}
                groupBy={(r) => r.designation}
                groupOrder={grid.groups.map((g) => g.designation)}
                groupTotals
                fileName={`MonthlyVisitSummary_${monthValue}`}
              />
            </div>

            {/* padding 0: the theme pads .table-list-card .table-responsive by 24px, and sticky cells
                stop at that padding edge - rows then scrolled through a strip above the pinned header
                and beside the pinned columns. */}
            <div className="table-responsive mt-2" style={{ maxHeight: "70vh", overflow: "auto", padding: 0 }}>
              <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 3 }}>
                  <tr>
                    <th style={stickyLeft(0, PERSON_W, { zIndex: 4, background: "#FBEAEC", color: MAROON })}>Person</th>
                    <th style={stickyLeft(PERSON_W, SOURCE_W, { ...EDGE_L, zIndex: 4, background: "#FBEAEC", color: MAROON })}>Source</th>
                    {days.map((d) => (
                      <th
                        key={d}
                        style={{ textAlign: "center", minWidth: 32, background: isToday(d) ? "#FFF3CD" : "#FBEAEC", color: MAROON, cursor: grid.grand.allDays[d] ? "pointer" : "default" }}
                        title={dayTitle(d)}
                        onClick={grid.grand.allDays[d] ? () => openDrill(`${sourceLabel} - ${dayTitle(d)}`, null, d) : undefined}
                      >
                        {d}
                        <div style={{ fontSize: 9, fontWeight: 400, color: isWeekend(d) ? "#B02A37" : "#6c757d" }}>{month.date(d).format("dd")}</div>
                      </th>
                    ))}
                    <th style={stickyRight(PTOTAL_W, TOTAL_W, { ...EDGE_R, zIndex: 4, background: "#FBEAEC", color: MAROON })}>Total</th>
                    <th style={stickyRight(0, PTOTAL_W, { zIndex: 4, background: "#FBEAEC", color: MAROON })} title="The person's client visits + collection visits + technical reports, shown when they have more than one">All sources</th>
                  </tr>
                </thead>
                <tbody>
                  {(isLoading || isFetching) && (
                    <tr><td colSpan={colCount} className="text-center text-muted py-3">Loading...</td></tr>
                  )}
                  {!isLoading && !isFetching && grid.groups.length === 0 && (
                    <tr><td colSpan={colCount} className="text-center text-muted py-3">No {sourceLabel.toLowerCase()} in {monthLabel}.</td></tr>
                  )}
                  {!isLoading && !isFetching && grid.groups.map((g) => {
                    const keys = g.people.map((p) => p.key);
                    return (
                      <React.Fragment key={g.designation}>
                        <tr>
                          <td colSpan={colCount} style={{ position: "sticky", left: 0, background: "#F1E4E7", fontWeight: 700, color: MAROON }}>
                            {g.designation}{" "}
                            <span className="fw-normal text-muted">({g.people.length} {g.people.length === 1 ? "person" : "people"}, {g.totals.total} visits)</span>
                          </td>
                        </tr>
                        {g.people.map((p) => renderBlock({
                          key: p.key,
                          personKeys: [p.key],
                          name: p.name,
                          lines: p.lines,
                          total: p.total,
                          label: (
                            <button
                              type="button"
                              className="btn btn-link p-0 d-flex align-items-center gap-2 text-start text-decoration-none"
                              onClick={() => openDrill(`${p.name} - ${monthLabel}`, [p.key], null)}
                              title="All of this person's visits this month"
                            >
                              <EmployeeAvatar name={p.name} photoUrl={photoUrls[p.employeeGuid]} />
                              <span style={{ minWidth: 0 }}>
                                <span className="tx-party d-block text-truncate" style={{ maxWidth: PERSON_W - 50 }}>{p.name}</span>
                                <span className="text-muted" style={{ fontSize: 10 }}>{activeDays(p)} day{activeDays(p) === 1 ? "" : "s"} active</span>
                              </span>
                            </button>
                          ),
                        }))}
                        {g.people.length > 1 && renderBlock({
                          key: `${g.designation}-total`, label: `${g.designation} total`, lines: g.totals.lines, total: g.totals.total,
                          personKeys: keys, bg: "#FAF5F6", strong: true,
                        })}
                      </React.Fragment>
                    );
                  })}
                  {!isLoading && !isFetching && grid.groups.length > 0 && renderBlock({
                    key: "grand", label: "Grand total", lines: grid.grand.lines, total: grid.grand.total, personKeys: null, bg: "#F8F9FA", strong: true,
                  })}
                </tbody>
              </table>
            </div>
            <p className="small text-muted mt-2 mb-0">
              <b style={{ color: VISIT_SOURCES.SALES.color }}>Client visits</b> = client visit reports logged on site (GPS, time of visit).{" "}
              <b style={{ color: VISIT_SOURCES.COLLECTIONS.color }}>Collection visits</b> = &quot;Visit&quot; entries from Collections (no payment collected); actual collections are not counted.{" "}
              <b style={{ color: VISIT_SOURCES.TECHNICAL.color }}>Technical</b> = service reports filed by the technician. Grouped by the person&apos;s employee designation.
            </p>
          </div>
        </div>
      </div>

      {drill && (
        <Modal show={!openSalesGuid} onHide={() => setDrill(null)} size="lg" centered scrollable>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6">
              {drill.title}{" "}
              {!drillLoading && <span className="badge rounded-pill ms-1" style={{ background: MAROON }}>{drillVisits.length}</span>}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: "#FAFAFB" }}>
            {drillLoading && <p className="text-muted mb-0">Loading...</p>}
            {!drillLoading && drillVisits.length === 0 && <p className="text-muted mb-0">No visits found.</p>}
            {!drillLoading && drillVisits.map((v, i) => {
              const at = dayjs(v.activityAt);
              const meta = VISIT_SOURCES[v.source] || VISIT_SOURCES.SALES;
              const t = timeLabel(v);
              const newDate = !drill.day && (i === 0 || !at.isSame(dayjs(drillVisits[i - 1].activityAt), "day"));
              return (
                <React.Fragment key={`${v.source}-${v.recordGuid}`}>
                  {newDate && <div className="fw-bold small mt-2 mb-1" style={{ color: MAROON }}>{at.format("dddd, DD MMM YYYY")}</div>}
                  <div className="d-flex gap-3 bg-white border rounded p-2 mb-2" style={{ borderLeft: `4px solid ${meta.color}` }}>
                    <div className="text-center flex-shrink-0" style={{ width: 70 }}>
                      <Clock size={14} style={{ color: meta.color }} />
                      <div className="fw-bold" style={{ fontSize: 18, color: v.timeKnown ? meta.color : "#adb5bd", lineHeight: 1.1 }}>{t.time}</div>
                      <div className="text-muted" style={{ fontSize: 10, lineHeight: 1.2 }}>{t.note}</div>
                      {!v.timeKnown && (
                        <div style={{ fontSize: 10, color: "#495057" }} className="mt-1">saved {dayjs(v.recordedAt).format("HH:mm")}</div>
                      )}
                      {v.savedInBatch && (
                        <span className="badge mt-1" style={{ background: "#FFF3CD", color: "#8a6100", fontSize: 9 }} title="Several entries saved within minutes of each other">batch</span>
                      )}
                      {v.source === "TECHNICAL" && v.timeKnown && v.timeOut && (
                        <div className="text-muted" style={{ fontSize: 10 }}>out {String(v.timeOut).slice(0, 5)}</div>
                      )}
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="d-flex justify-content-between gap-2 flex-wrap">
                        <div>
                          <span className="tx-party">{v.custName || "-"}</span>
                          {v.siteLabel && <span className="text-muted small"> · {v.siteLabel}</span>}
                          <span className="text-muted small"> · {[v.refNumber, v.branchCode].filter(Boolean).join(" · ")}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge rounded-pill" style={{ background: meta.tint, color: meta.color }}>
                            <meta.Icon size={10} className="me-1" />{meta.label}{v.subType ? ` · ${JOB_KIND[v.subType] || v.subType}` : ""}
                          </span>
                          {v.hasChecklist && <span className="badge badge-linesuccess d-inline-flex align-items-center gap-1"><CheckSquare size={11} /> Checklist</span>}
                          <NavigateLink latitude={v.latitude} longitude={v.longitude} label="Map" className="small" />
                          {v.source === "SALES" && (
                            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setOpenSalesGuid(v.recordGuid)} title="Full visit report">
                              <ExternalLink size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                      {(!drill.personKeys || drill.personKeys.length > 1) && (
                        <div className="small d-flex align-items-center gap-1 mt-1">
                          <EmployeeAvatar name={v.personName} photoUrl={drillPhotos[v.employeeRecordGuid]} />
                          <span className="fw-semibold">{v.personName}</span>
                          <span className="text-muted">· {v.designationName}</span>
                        </div>
                      )}
                      {v.contactPerson && (
                        <div className="small text-muted mt-1 d-flex align-items-center gap-1">
                          <User size={12} /> {v.contactPerson}
                          {v.contactDesignation ? ` (${v.contactDesignation})` : ""}
                          {v.contactMobile ? ` · ${v.contactMobile}` : ""}
                        </div>
                      )}
                      <div className="small mt-1" style={{ color: "#1d2433", whiteSpace: "pre-wrap" }}>
                        <strong>{v.source === "TECHNICAL" ? "Technician:" : "Remarks:"}</strong> {v.remarks || "-"}
                      </div>
                      {v.clientRemarks && (
                        <div className="small mt-1" style={{ color: "#1F5FAD", whiteSpace: "pre-wrap" }}>
                          <strong>{v.source === "TECHNICAL" ? "Customer:" : "Client:"}</strong> {v.clientRemarks}
                        </div>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </Modal.Body>
          <Modal.Footer>
            <span className="me-auto small text-muted">
              Large time = a real on-site time (client visit GPS, technical QR check-in). Otherwise only the save time is known - entries saved together are tagged               Time = on site for client visits, when logged for collection visits, when the report was filed for technical.quot;batch              Time = on site for client visits, when logged for collection visits, when the report was filed for technical.quot;.
            </span>
            <Button variant="secondary" onClick={() => setDrill(null)}>Close</Button>
          </Modal.Footer>
        </Modal>
      )}

      {openSalesGuid && (
        <SalesVisitDetail
          recordGuid={openSalesGuid}
          photoUrl={drillPhotos[drillVisits.find((v) => v.recordGuid === openSalesGuid)?.employeeRecordGuid]}
          onClose={() => setOpenSalesGuid(null)}
        />
      )}
    </div>
  );
};

export default ClientVisitMonthlySummaryPage;
